import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { patchReport, TEMPLATE_DEFAULTS, TEMPLATE_LABELS, brandLabel } from '@/lib/api';
import type {
  Report,
  ReportDisplayOptions,
  ReportStatus,
  ReportTemplateType,
} from '@/lib/api';

export interface ReportEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: Report | null;
  /** Called after successful patch — parent should refresh */
  onSaved: () => void;
}

function deepEqualOptions(a: ReportDisplayOptions, b: ReportDisplayOptions): boolean {
  return (
    a.show_donut_units === b.show_donut_units &&
    a.show_all_skus === b.show_all_skus &&
    a.show_internal_columns === b.show_internal_columns
  );
}

export function ReportEditModal({ open, onOpenChange, report, onSaved }: ReportEditModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ReportStatus>('draft');
  const [templateType, setTemplateType] = useState<ReportTemplateType>('partner');
  const [introText, setIntroText] = useState('');
  // Effective display options — starts as override (if set) or template defaults.
  const [options, setOptions] = useState<ReportDisplayOptions>(TEMPLATE_DEFAULTS.partner);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && report) {
      setTitle(report.title);
      setDescription(report.description ?? '');
      setStatus(report.status);
      setTemplateType(report.template_type);
      setIntroText(report.intro_text ?? '');
      setOptions(report.display_options ?? TEMPLATE_DEFAULTS[report.template_type]);
      setError(null);
    }
  }, [open, report]);

  // When admin switches template, reset options to that template's defaults.
  // (Admin can then tinh chỉnh từng checkbox qua section "Cài đặt hiển thị" bên dưới.)
  const handleTemplateChange = (next: ReportTemplateType) => {
    setTemplateType(next);
    setOptions(TEMPLATE_DEFAULTS[next]);
  };

  const isCustomized = useMemo(
    () => !deepEqualOptions(options, TEMPLATE_DEFAULTS[templateType]),
    [options, templateType]
  );

  if (!report) return null;

  const originalOptions = report.display_options ?? TEMPLATE_DEFAULTS[report.template_type];
  const dirty =
    title.trim() !== report.title ||
    (description.trim() || null) !== report.description ||
    status !== report.status ||
    templateType !== report.template_type ||
    (introText.trim() || null) !== (report.intro_text ?? null) ||
    !deepEqualOptions(options, originalOptions);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Tiêu đề không được trống');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await patchReport(report.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        status,
        template_type: templateType,
        // Save full override only if customized; else null = follow template defaults.
        display_options: isCustomized ? options : null,
        intro_text: introText.trim() ? introText.trim() : null,
      });
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Chỉnh sửa báo cáo</DialogTitle>
          <DialogDescription>
            Cập nhật metadata + loại báo cáo. Hãng và period immutable — nếu cần đổi, hãy tạo báo cáo mới.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 rounded-md bg-muted/40 p-3 text-xs">
            <div>
              <div className="text-muted-foreground">Hãng</div>
              <div className="font-semibold">{brandLabel(report.brand)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Period</div>
              <div className="font-semibold">{report.period_label}</div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="edit-title" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Tiêu đề <span className="text-destructive">*</span>
            </label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              disabled={submitting}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="edit-desc" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Mô tả
            </label>
            <Textarea
              id="edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              className="min-h-[80px]"
              disabled={submitting}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
            <Select value={status} onValueChange={(v) => setStatus(v as ReportStatus)} disabled={submitting}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft (chưa share được)</SelectItem>
                <SelectItem value="published">Published (sẵn sàng share)</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Template type — switching resets section toggles to that template's defaults */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Loại báo cáo
            </label>
            <Select value={templateType} onValueChange={(v) => handleTemplateChange(v as ReportTemplateType)} disabled={submitting}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(TEMPLATE_LABELS) as ReportTemplateType[]).map((key) => {
                  const t = TEMPLATE_LABELS[key];
                  const Icon = t.icon;
                  return (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-medium">{t.label}</span>
                        <span className="text-muted-foreground">— {t.description}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              Đổi loại sẽ reset toggles bên dưới về mặc định của loại đó. Sau đó admin có thể tinh chỉnh thêm.
            </p>
          </div>

          {/* Section toggles — override per-report */}
          <div className="space-y-2 rounded-md border bg-muted/30 p-3">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Cài đặt hiển thị
              </div>
              {isCustomized && (
                <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                  Customized
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Tinh chỉnh từng section. Khi tất cả khớp default của template, tự động "đồng bộ" — không cần override.
            </p>

            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 cursor-pointer accent-primary"
                checked={options.show_donut_units}
                onChange={(e) => setOptions({ ...options, show_donut_units: e.target.checked })}
                disabled={submitting}
              />
              <span>
                <span className="font-medium">Hiển thị donut "Số lượng bán"</span>
                <span className="ml-1 text-muted-foreground">— bên cạnh donut Doanh thu</span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 cursor-pointer accent-primary"
                checked={options.show_all_skus}
                onChange={(e) => setOptions({ ...options, show_all_skus: e.target.checked })}
                disabled={submitting}
              />
              <span>
                <span className="font-medium">Hiển thị bảng "Toàn bộ SKU Active"</span>
                <span className="ml-1 text-muted-foreground">— ngoài bảng Focal Point</span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 cursor-pointer accent-primary"
                checked={options.show_internal_columns}
                onChange={(e) => setOptions({ ...options, show_internal_columns: e.target.checked })}
                disabled={submitting}
              />
              <span>
                <span className="font-medium">Hiển thị cột nội bộ trong bảng SKU</span>
                <span className="ml-1 text-muted-foreground">— Giá CT, % LN (ẩn khi share cho hãng)</span>
              </span>
            </label>
          </div>

          {/* Intro text */}
          <div className="space-y-1.5">
            <label htmlFor="edit-intro" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Lời mở đầu
              <span className="ml-1 font-normal text-muted-foreground">— max 500 ký tự</span>
            </label>
            <Textarea
              id="edit-intro"
              value={introText}
              onChange={(e) => setIntroText(e.target.value)}
              maxLength={500}
              className="min-h-[72px]"
              disabled={submitting}
              placeholder="vd: Quý này hãng push QD-OLED, sales tăng 32%..."
            />
            <p className="text-[11px] text-muted-foreground">
              Hiển thị trên đầu báo cáo. Plain text — line breaks giữ nguyên. Để trống nếu không cần.
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Hủy
          </Button>
          <Button onClick={handleSubmit} disabled={!dirty || submitting || !title.trim()}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Lưu thay đổi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
